import { Fragment, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, Typography } from '@mui/material';
import { useAppSelector } from '../../../../redux/ReduxHooks';
import { blurFocusedElement, captureFocusedElement, restoreFocusToElement, type FocusableElement } from '../../../../utils/focusRestore';
import PortControl from '../../../Drawers/Electronics/PortControl';
import PortSlotIcon from '../../../Icons/PortSlotIcon';
import { DRIVE_UNIT_PORT_LEGEND_SIZING, DRIVE_UNIT_PORT_MAX, DRIVE_UNIT_PORT_MIN } from '../../../../utils/driveUnitPortSelection';

interface DriveUnitPortBoardProps {
  id: string;
  selected: number;
  onPortClick: (portNumber: number) => void;
  /**
   * `dialog` (default): confirm before taking a port used elsewhere; compact legend (configure drawer).
   * `inline`: select the port and let the parent show conflict messaging; full-size legend (host dialogs).
   */
  conflictPresentation?: 'dialog' | 'inline';
}

const DRIVE_UNIT_PORT_COUNT = DRIVE_UNIT_PORT_MAX - DRIVE_UNIT_PORT_MIN + 1;

function isPortClaimedByCard(
  driveUnitPorts: Array<{ driveUnitPort: number; electronic: string }>,
  portNumber: number,
  electronicId: string,
  selected: number
): boolean {
  return driveUnitPorts.some((row) => row.driveUnitPort === portNumber && row.electronic === electronicId) || selected === portNumber;
}

function isPortClaimedByAnotherCard(
  driveUnitPorts: Array<{ driveUnitPort: number; electronic: string }>,
  portNumber: number,
  electronicId: string
): boolean {
  return driveUnitPorts.some((row) => row.driveUnitPort === portNumber && row.electronic !== electronicId);
}

type LegendSwatchProps = {
  selected?: boolean;
  portUsedByOtherDevice?: boolean;
  label: string;
  iconSize: number;
  fontSize: number | string;
  gap: number;
};

const LegendSwatch = ({ selected, portUsedByOtherDevice, label, iconSize, fontSize, gap }: LegendSwatchProps) => (
  <Stack direction="row" spacing={gap} alignItems="center">
    <PortSlotIcon decorative size={iconSize} selected={selected} portUsedByOtherDevice={portUsedByOtherDevice} />
    <Typography color="text.secondary" fontSize={fontSize}>
      {label}
    </Typography>
  </Stack>
);

const DriveUnitPortBoard = ({ id, selected, onPortClick, conflictPresentation = 'dialog' }: DriveUnitPortBoardProps) => {
  const { t } = useTranslation();
  const [conflictOpen, setConflictOpen] = useState(false);
  const [pendingPort, setPendingPort] = useState<number | null>(null);
  const focusTriggerRef = useRef<FocusableElement | null>(null);
  const driveUnitPorts = useAppSelector((state) => state.application.driveUnitPorts);
  const hasValidSelection = Number.isFinite(selected) && selected >= DRIVE_UNIT_PORT_MIN && selected <= DRIVE_UNIT_PORT_MAX;
  const legendSizing = DRIVE_UNIT_PORT_LEGEND_SIZING[conflictPresentation];

  const portStatus = useMemo(
    () =>
      Array.from({ length: DRIVE_UNIT_PORT_COUNT }, (_, index) => {
        const portNumber = DRIVE_UNIT_PORT_MIN + index;
        return Number.isFinite(selected) && selected === portNumber;
      }),
    [selected]
  );

  const portUsedByOther = useMemo(
    () =>
      Array.from({ length: DRIVE_UNIT_PORT_COUNT }, (_, index) => {
        const portNumber = DRIVE_UNIT_PORT_MIN + index;
        const claimedByThisCard = isPortClaimedByCard(driveUnitPorts, portNumber, id, selected);
        const claimedByAnotherCard = isPortClaimedByAnotherCard(driveUnitPorts, portNumber, id);
        return claimedByAnotherCard && !claimedByThisCard;
      }),
    [driveUnitPorts, id, selected]
  );

  const handlePortClick = (portNumber: number) => {
    const claimedByThisCard = isPortClaimedByCard(driveUnitPorts, portNumber, id, selected);
    const claimedByAnotherCard = isPortClaimedByAnotherCard(driveUnitPorts, portNumber, id);
    const usedByOtherControl = claimedByAnotherCard && !claimedByThisCard;

    if (usedByOtherControl && conflictPresentation === 'dialog') {
      // Dialog aria-hides #root; blur the focused port <g> first to avoid an a11y warning,
      // then restore that trigger when the dialog exits (MUI would otherwise restore body).
      const trigger = captureFocusedElement();
      focusTriggerRef.current = trigger;
      blurFocusedElement(trigger);
      setPendingPort(portNumber);
      setConflictOpen(true);
      return;
    }

    onPortClick(portNumber);
  };

  const closeConflictDialog = () => {
    setConflictOpen(false);
    setPendingPort(null);
  };

  const handleContinue = () => {
    if (pendingPort != null) {
      onPortClick(pendingPort);
    }
    closeConflictDialog();
  };

  const handleCancel = () => {
    closeConflictDialog();
  };

  const handleConflictDialogExited = () => {
    const trigger = focusTriggerRef.current;
    focusTriggerRef.current = null;
    restoreFocusToElement(trigger);
  };

  return (
    <Fragment>
      <Stack spacing={1} alignItems="center" sx={{ width: '100%' }}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {hasValidSelection
            ? t('portSelector.selected', {
                port: selected,
                defaultValue: 'Selected port: {{port}}',
              })
            : t('portSelector.selectedNone', { defaultValue: 'No port selected' })}
        </Typography>
        <PortControl
          portStatus={portStatus}
          portUsedByOther={portUsedByOther}
          onPortClick={handlePortClick}
          conflictPresentation={conflictPresentation}
        />
        <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
          <LegendSwatch selected label={t('portSelector.legend.selected', { defaultValue: 'Selected Port' })} {...legendSizing} />
          <LegendSwatch portUsedByOtherDevice label={t('portSelector.legend.inUse', { defaultValue: 'Configured Port' })} {...legendSizing} />
          <LegendSwatch label={t('portSelector.legend.available', { defaultValue: 'Available Port' })} {...legendSizing} />
        </Stack>
      </Stack>
      <Dialog
        open={conflictPresentation === 'dialog' && conflictOpen}
        onClose={handleCancel}
        disableRestoreFocus
        slotProps={{ transition: { onExited: handleConflictDialogExited } }}
        aria-labelledby="drive-unit-port-conflict-title"
        aria-describedby="drive-unit-port-conflict-desc"
      >
        <DialogTitle id="drive-unit-port-conflict-title">{t('portSelector.conflict.title', { defaultValue: 'Port in use' })}</DialogTitle>
        <DialogContent>
          <DialogContentText id="drive-unit-port-conflict-desc" color="text.warning">
            {t('portSelector.conflict.message', {
              defaultValue: 'This port is already used by another mechanism or electronic in your code.',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>{t('portSelector.conflict.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={handleContinue} color="warning" variant="contained" autoFocus>
            {t('portSelector.conflict.continue', { defaultValue: 'Continue' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default DriveUnitPortBoard;
